import { AxiosError } from 'axios'
import { action, computed, makeObservable, observable } from 'mobx'
import { distinctUntilChanged, Observable, Subscription } from 'rxjs'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { TableSortOrder } from 'components/Table'
import Document from 'documents/Document'
import DocumentStore from 'documents/DocumentStore'
import DocumentTypeStore from 'documents/DocumentTypeStore'
import { DocumentDetailsRootViewModel } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import Member from 'iam/models/Member'
import { GetCurrentMember } from 'iam/SessionStore'
import { Membership } from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import JobStore from 'jobs/JobStore'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import { Direction, Id } from 'types/definitions'
import { tapAction } from 'utils/mobx-util'
import { pluralize } from 'utils/pluralization-util'
import { DocumentSearchViewModel } from './DocumentSearchViewModel'
import { DocumentsLoadFilterItemsAdapter } from './DocumentsLoadFilterItemsAdapter'
import {
  DocumentsTableColumns,
  StaticColumnDetails,
} from './DocumentTableColumns'

interface DocumentActionResult {
  documentLongId: Id
  success?: boolean
  error?: AxiosError
}

export class DocumentManagementViewModel {
  /**
   * Subscription to workspace selector updates.
   */
  private subscription = new Subscription()

  /**
   * The set of selected document long ids.
   */
  @observable
  selectedDocumentLongIds = new Set<Id>()

  /**
   * The document management table columns.
   */
  @observable
  columns = DocumentsTableColumns

  confirmDocumentArchive = new ConfirmViewModel()

  loadFilterItemsAdapter = new DocumentsLoadFilterItemsAdapter(
    () => this.availableWorkspaces,
    this.documentTypeStore,
    this.jobSearchStore,
  )

  searcher = new DocumentSearchViewModel(
    () => this.userPublicId,
    this.loadFilterItemsAdapter,
    this.documentStore,
    this.jobStore,
    this.createFilterStorage('UserDetailsDocumentManagement'),
    this.newAlert,
  )

  /**
   * Update workspaces filter when current workspace is changed.
   */
  private workspaceSelectorUpdates$ = this.currentMember$.pipe(
    distinctUntilChanged(),
    tapAction((member) => {
      if (
        this.searcher.workspacesFilter.options.some(
          (o) => o.value === member.workspace.id,
        )
      ) {
        this.searcher.workspacesFilter.change([member.workspace.id])
      }
    }),
  )

  constructor(
    private _userPublicId: () => Id,
    private _userMemberships: () => Membership[],
    private _currentUserMemberships: () => Membership[],
    private documentDetailsRootVM: DocumentDetailsRootViewModel,
    private documentStore: DocumentStore,
    private documentTypeStore: DocumentTypeStore,
    private jobStore: JobStore,
    private jobSearchStore: JobSearchStore,
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private createFilterStorage: FilterStorageFactory,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  async activate() {
    this.searcher.activate()
    await this.searcher.workspacesFilter.activate()
    this.subscription = this.workspaceSelectorUpdates$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.confirmDocumentArchive.decline()
    this.subscription.unsubscribe()
    this.searcher.deactivate()
  }

  /**
   * The currently selected user.
   */
  @computed
  get userPublicId() {
    return this._userPublicId()
  }

  /**
   * The currently selected user memberships.
   */
  @computed
  get userMemberships() {
    return this._userMemberships()
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * (in regard to managing documents) AND that the current user is a member of.
   */
  @computed
  get availableWorkspaces() {
    try {
      const isPlatformAdmin = this.getCurrentMember().user.platformAdmin
      const userWorkspaces = this.userMemberships.map((m) => m.workspace)

      if (isPlatformAdmin) {
        return userWorkspaces
      }

      const adminWorkspaces = this._currentUserMemberships()
        .filter((m) => m.member.hasPermission('ADMIN_DOCUMENT_MANAGE'))
        .map((m) => m.workspace)

      return userWorkspaces.filter((uw) => adminWorkspaces.includes(uw))
    } catch {
      return []
    }
  }

  /**
   * Whether the document with the given id is selected.
   */
  isSelected(documentLongId: Id) {
    return this.selectedDocumentLongIds.has(documentLongId)
  }

  /**
   * Toggles the selection of the document with the given id.
   */
  @action.bound
  toggleDocument(checked: boolean, documentLongId: Id) {
    if (checked) {
      this.selectedDocumentLongIds.add(documentLongId)
    } else {
      this.selectedDocumentLongIds.delete(documentLongId)
    }
  }

  /**
   * Gets the sort order for the specified column.
   */
  getSortOrder(column: StaticColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? parseDirection(this.searcher.sorting.direction)
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  setSortOrder(column: StaticColumnDetails, direction: TableSortOrder) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(toDirection(direction))
  }

  /**
   * Shows the document details modal for the given document.
   */
  @action.bound
  showDocumentDetails(document: Document) {
    this.documentDetailsRootVM.viewDocumentDetails(document, true)
  }

  /**
   * Triggers the download of the selected documents (latest version only).
   */
  @action.bound
  async downloadDocuments(document?: Document) {
    const documents = (
      document
        ? [document]
        : this.documentStore.documents.filter((doc) =>
            this.selectedDocumentLongIds.has(doc.id),
          )
    ).filter((doc) => doc.downloadable)

    // Ensure there's at least one document to download.
    if (documents.length < 1) {
      this.newAlert()
        .withState(AlertState.Neutral)
        .saying(
          'Not a valid selection.',
          'Select one or more documents to download.',
        )
        .autoDismiss(2500)
        .show()

      return
    }

    try {
      // If more than one document is selected, we'll use the zip download option.
      if (documents.length === 1) {
        this.documentStore.download(documents[0], false)
      } else {
        // If they are downloadable, they should have a latest version.
        const zipFilesPayload = documents.map((doc) => doc.id)

        await this.documentStore.downloadAll(zipFilesPayload)
      }

      this.newAlert()
        .withState(AlertState.Success)
        .saying('Success!', 'Your download has started!')
        .autoDismiss(2500)
        .show()

      // Deselect documents.
      documents.forEach((r) => this.toggleDocument(false, r.id))
    } catch (error) {
      console.error(error)

      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          `Failed to download ${pluralize('document', documents.length)}.`,
        )
        .autoDismiss(2500)
        .show()
    }
  }

  /**
   * Triggers the archival of the selected documents.
   */
  @action.bound
  async archiveDocuments(document?: Document) {
    // Can't archive documents that are already archived.
    const documents = (
      document
        ? [document]
        : this.documentStore.documents.filter((doc) =>
            this.selectedDocumentLongIds.has(doc.id),
          )
    ).filter((doc) => !doc.archived)

    // Ensure there's at least one document to archive.
    if (documents.length < 1) {
      this.newAlert()
        .withState(AlertState.Neutral)
        .saying(
          'Not a valid selection.',
          'Select one or more documents that are not currently archived.',
        )
        .autoDismiss(2500)
        .show()

      return
    }

    // Ensure that the user confirms.
    if (!(await this.confirmDocumentArchive.showForBulk(documents.length))) {
      return
    }

    // Tabulate successful/failed archivals.
    const results: DocumentActionResult[] = await Promise.all(
      documents.map((doc) =>
        this.documentStore
          .archiveDocument(Id.assert(doc.id))
          .then(() => {
            return { documentLongId: doc.id, success: true }
          })
          .catch((error: AxiosError) => {
            return { documentLongId: doc.id, error }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // Display appropriate alert.
    if (failed.length < 1) {
      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${successful.length} ${pluralize('document has', successful.length, 'documents have')} been removed!`,
        )
        .autoDismiss(2500)
        .show()
    } else {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          `Failed to remove ${failed.length} ${pluralize('document', failed.length)}.`,
        )
        .autoDismiss(2500)
        .show()
    }

    // Deselect successfully archived documents.
    successful.forEach((r) => this.toggleDocument(false, r.documentLongId))
  }
}

function parseDirection(direction?: Direction) {
  switch (direction) {
    case Direction.Ascending:
      return 'asc'
    case Direction.Descending:
      return 'desc'
    default:
      return 'none'
  }
}

function toDirection(sortOrder: TableSortOrder) {
  switch (sortOrder) {
    case 'asc':
      return Direction.Ascending
    case 'desc':
      return Direction.Descending
    default:
      return undefined
  }
}
